/**
 * Who holds each of a race's coins, as far as this phone knows (docs/LIVE.md, "Coins"). Both lawns
 * carry the same coins; each goes to whichever mower reached it first, by race ticks (the same tick:
 * nobody). This phone sees its own pickups at once and the friend's a moment later (their ghost
 * runs behind), so a coin it took can turn out to be the friend's. The room settles the coins for
 * the payout; this is for what the lawn and the HUD show meanwhile. Pure.
 */

/** What a friend's pickup means here. */
export type FriendPickup =
  /** This player never had it: it vanishes from their lawn. */
  | 'taken'
  /** This player had it, but the friend got there first: it goes. */
  | 'takenBack'
  /** Both reached it on the same tick: nobody's. */
  | 'tied'
  /** This player was first: nothing changes. */
  | 'late';

export class RaceCoins {
  private readonly mine: number[];
  private readonly friend: number[];

  constructor(count: number) {
    this.mine = Array.from({ length: count }, () => -1);
    this.friend = Array.from({ length: count }, () => -1);
  }

  /** This player's mower picked up coin `i` at tick `t`. Whether they hold it now. */
  mineAt(i: number, t: number): boolean {
    if (this.mine[i] === undefined) return false;
    if (this.mine[i] === -1) this.mine[i] = t;
    return this.holds(i);
  }

  /** The friend's mower picked up coin `i` at tick `t`. */
  friendAt(i: number, t: number): FriendPickup {
    if (this.friend[i] === undefined) return 'late';
    if (this.friend[i] === -1) this.friend[i] = t;
    const mine = this.mine[i]!;
    if (mine === -1) return 'taken';
    return this.friend[i] < mine ? 'takenBack' : this.friend[i] === mine ? 'tied' : 'late';
  }

  /** Whether coin `i` is this player's (as far as this phone knows). */
  holds(i: number): boolean {
    const mine = this.mine[i] ?? -1;
    const friend = this.friend[i] ?? -1;
    return mine !== -1 && (friend === -1 || mine < friend);
  }

  /** Whether the friend reached coin `i` before (or with) this player: it is gone from this lawn. */
  gone(i: number): boolean {
    const friend = this.friend[i] ?? -1;
    const mine = this.mine[i] ?? -1;
    return friend !== -1 && (mine === -1 || friend <= mine);
  }

  /** Coins this player holds. */
  get held(): number {
    return this.mine.filter((_, i) => this.holds(i)).length;
  }
}
